import { HttpEvent, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { toast } from 'ngx-sonner';
import { catchError, NEVER, Observable } from 'rxjs';

/**
 * Catches errors and shows a toast with the error message.
 *
 * @param apiSegment - The starting segment of the API URL. Only requests starting with this segment will be intercepted
 */
export const errorInterceptor: (apiSegment?: string) => HttpInterceptorFn =
    (apiSegment?: string) =>
    (req, next): Observable<HttpEvent<unknown>> => {
        if (apiSegment && !req.url.startsWith(apiSegment)) {
            return next(req);
        }

        const translate = inject(TranslateService);

        return next(req).pipe(
            catchError((error, caught) => {
                if (error.status === 429) {
                    toast.error(translate.instant('UTIL_ERRORS.HTTP_429.TITLE'), {
                        description: translate.instant(
                            'UTIL_ERRORS.HTTP_429.DESCRIPTION'
                        ),
                        duration: 6000
                    });
                    return NEVER;
                } else if (error.status >= 400) {
                    toast.error(
                        translate.instant('UTIL_ERRORS.HTTP_4XX.TITLE', {
                            status: error.status
                        }),
                        {
                            description: error.error ?? error.statusText,
                            duration: 6000
                        }
                    );
                    return NEVER;
                }

                return caught;
            })
        );
    };
